import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../../../../../utils/axiosInstance";
import BackButton from "../../../../../../components/Button/BackButton";

import JobStatusStepper from "./JobStatusStepper";
import JobDetailsInfoCard from "./JobDetailsInfoCard";
import AssignmentStatusCard from "./AssignmentStatusCard";
import ChecklistAnswersCard from "./ChecklistAnswersCard";
import VendorRatingCard from "./VendorRatingCard";
import PayablesToVendorCard from "./PayablesToVendorCard";
import LinkedInputFilesCard from "./LinkedInputFilesCard";
import OutputFilesCard from "./OutputFilesCard";
import MessagesCard from "./MessagesCard";
import { toast, confirmDialog } from "../../../../../../components/Toast/toast";

const JobDetailPage = () => {
  const { id: projectId, jobId } = useParams();
  const navigate = useNavigate();

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [inputFiles, setInputFiles] = useState([]);
  const [outputFiles, setOutputFiles] = useState([]);

  // Fetch job details
  useEffect(() => {
    const fetchJobDetails = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/jobs/${jobId}`, {
          withCredentials: true,
        });
        setJob(response.data.data);
      } catch (err) {
        console.error("Failed to fetch job details:", err);
        setError("Failed to load job details");
      } finally {
        setLoading(false);
      }
    };

    if (jobId) {
      fetchJobDetails();
    }
  }, [jobId]);

  const fetchJobInputFiles = useCallback(async () => {
    try {
      const response = await api.get(`/job-input-files?job_id=${jobId}`, {
        withCredentials: true,
      });
      setInputFiles(response.data.data || []);
    } catch (error) {
      console.error("Failed to fetch job input files:", error);
    }
  }, [jobId]);

  useEffect(() => {
    fetchJobInputFiles();
  }, [jobId, fetchJobInputFiles]);

  const fetchJobOutputFiles = useCallback(async () => {
    try {
      const response = await api.get(`/job-output-files?job_id=${jobId}`, {
        withCredentials: true,
      });
      setOutputFiles(response.data.data || []);
    } catch (error) {
      console.error("Failed to fetch job output files:", error);
    }
  }, [jobId]);

  useEffect(() => {
    fetchJobOutputFiles();
  }, [jobId, fetchJobOutputFiles]);

  // Refresh job data
  const refreshJob = async () => {
    try {
      const response = await api.get(`/jobs/${jobId}`, {
        withCredentials: true,
      });
      setJob(response.data.data);
    } catch (err) {
      console.error("Failed to refresh job:", err);
    }
  };

  // Handler: Download PO
  const handleDownloadPO = () => {
    // TODO: Implement PO download
    console.log("Downloading PO for job:", jobId);
    toast.error("PO download feature coming soon!");
  };

  // Handler: Clone job
  const handleClone = () => {
    navigate(`/project/${projectId}/create-job?clone=${jobId}`);
  };

  // Handler: Update job
  const handleUpdate = () => {
    navigate(`/project/${projectId}/job/${jobId}/edit`);
  };

  // Handler: Delete job
  const handleDelete = async () => {
    if (!await confirmDialog("Are you sure you want to delete this job?")) {
      return;
    }

    try {
      await api.delete(`/jobs/${jobId}`, { withCredentials: true });
      toast.success("Job deleted successfully");
      navigate(`/project/${projectId}?tab=jobs`);
    } catch (err) {
      console.error("Failed to delete job:", err);
      toast.error(err.response?.data?.message || "Failed to delete job");
    }
  };

  // Handler: Status changes
  const handleStatusChange = async (newStatus, comment = "") => {
    try {
      await api.post(
        `/jobs/${jobId}/status`,
        {
          new_status: newStatus,
          comment: comment,
          changed_by: "admin",
        },
        { withCredentials: true }
      );
      await refreshJob();
      toast.error(`Job status changed to ${newStatus}`);
    } catch (err) {
      console.error("Failed to change status:", err);
      toast.error(err.response?.data?.message || "Failed to change job status");
    }
  };

  const handleOffer = () => handleStatusChange("Offered to Vendor");
  const handleStart = async () => {
    try {
      await api.post(`/jobs/${jobId}/start`, {}, { withCredentials: true });
      await refreshJob();
      toast.success("Job started successfully");
    } catch (err) {
      console.error("Failed to start job:", err);
      toast.error(err.response?.data?.message || "Failed to start job");
    }
  };
  const handleAccept = () => handleStatusChange("Completion Accepted");
  const handleReject = () => {
    const comment = prompt("Enter reason for rejection:");
    if (comment !== null) {
      handleStatusChange("Completion Rejected", comment);
    }
  };
  const handleCancel = () => {
    const comment = prompt("Enter reason for cancellation:");
    if (comment !== null) {
      handleStatusChange("Cancelled", comment);
    }
  };
  const handleDraft = () => handleStatusChange("Draft");

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <div className="animate-spin rounded-full h-8 w-8 border border-gray-300 border-t-brand-600" />
      </div>
    );
  }

  if (error) return <p className="text-danger-600 text-sm">{error}</p>;
  if (!job) return <p className="text-sm text-gray-500">Job not found.</p>;

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <BackButton />
        <h1 className="text-[22px] font-semibold tracking-tight text-gray-900">Job details</h1>
      </div>

      <div className="bg-white border border-gray-100 shadow-sm rounded-lg p-5">
        <JobStatusStepper current={job.status} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <div className="lg:col-span-6 space-y-5">
          <JobDetailsInfoCard
            job={job}
            onDownloadPO={handleDownloadPO}
            onClone={handleClone}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
          />
        </div>

        <div className="lg:col-span-6 space-y-5">
          <AssignmentStatusCard
            job={job}
            onAccept={handleAccept}
            onReject={handleReject}
            onCancel={handleCancel}
            onDraft={handleDraft}
            onOffer={handleOffer}
            onStart={handleStart}
          />
          <ChecklistAnswersCard job={job} />
          <VendorRatingCard job={job} />
        </div>
      </div>

      <PayablesToVendorCard job={job} />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <div className="lg:col-span-6">
          <LinkedInputFilesCard
            jobId={jobId}
            files={inputFiles}
            onRefresh={fetchJobInputFiles}
          />
        </div>

        <div className="lg:col-span-6">
          <OutputFilesCard
            jobId={jobId}
            files={outputFiles}
            onRefresh={fetchJobOutputFiles}
          />
        </div>
      </div>

      <MessagesCard job={job} />
    </div>
  );
};

export default JobDetailPage;